import { useState } from 'react';
import { collection, doc, Timestamp, writeBatch } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { useAdminStore } from '../store/adminStore';
import { normalizeAdminRole } from './useAdminRole';

export default function useAdminAction() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const showAuditToast = useAdminStore((state) => state.showAuditToast);
  const profile = useAdminStore((state) => state.profile);

  const performAction = async ({
    action,
    targetBusinessId = null,
    targetType = null,
    targetId = null,
    before = null,
    after = null,
    reason = '',
    severity = 'INFO',
    requiresApproval = false,
    approvalPayload = null,
    writeFn,
  }) => {
    if (!auth.currentUser?.email) {
      throw new Error('Admin is not authenticated.');
    }

    if (!requiresApproval && typeof writeFn !== 'function') {
      throw new Error('writeFn(batch, db) is required.');
    }

    setLoading(true);
    setError(null);

    try {
      const batch = writeBatch(db);
      const logRef = doc(collection(db, 'adminAuditLog'));
      const now = Timestamp.now();
      const normalizedSeverity = ['INFO', 'WARNING', 'CRITICAL'].includes(severity) ? severity : 'INFO';
      const adminEmail = auth.currentUser.email;
      const normalizedTargetId = targetBusinessId ?? targetId ?? null;

      if (requiresApproval) {
        const taskRef = doc(collection(db, 'adminTasks'));
        batch.set(taskRef, {
          action,
          targetType,
          targetId: normalizedTargetId,
          targetBusinessId: targetBusinessId ?? null,
          before: before ?? null,
          after: after ?? null,
          reason: reason || '',
          severity: normalizedSeverity,
          payload: approvalPayload ?? null,
          status: 'pending_approval',
          requestedBy: adminEmail,
          requestedByRole: normalizeAdminRole(profile?.role),
          createdAt: now,
          updatedAt: now,
        });
      }

      batch.set(logRef, {
        action,
        targetBusinessId: targetBusinessId ?? null,
        targetType,
        targetId: normalizedTargetId,
        before: before ?? null,
        after: after ?? null,
        reason: reason || '',
        severity: normalizedSeverity,
        requiresApproval,
        performedBy: adminEmail,
        performedByRole: normalizeAdminRole(profile?.role),
        timestamp: now,
      });

      if (!requiresApproval) writeFn(batch, db);
      await batch.commit();
      showAuditToast(requiresApproval ? 'Approval request queued. Audit logged.' : 'Action complete. Audit logged.');
    } catch (err) {
      const message = err?.message || 'Unknown admin action error';
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  };

  const execute = async ({
    action,
    targetBusinessId = null,
    targetType = null,
    targetId = null,
    before = null,
    after = null,
    reason = '',
    severity = 'INFO',
    requiresApproval = false,
    approvalPayload = null,
    applyChange,
  }) => performAction({
    action,
    targetBusinessId: targetBusinessId ?? targetId ?? null,
    targetType,
    targetId,
    before,
    after,
    reason,
    severity,
    requiresApproval,
    approvalPayload,
    writeFn: applyChange,
  });

  return { performAction, execute, loading, error };
}
